/**
 * GENERATED FILE — do not edit.
 *
 * Source: src/index.css. Regenerate with `npm run build:design-tokens`.
 * `npm run guard:design-tokens` fails when this is stale, which is what makes
 * the token page's "verified against index.css" claim true rather than hopeful.
 */

export interface TokenRow {
  token: string;
  role: string;
  light: string;
  dark: string;
}

export interface TokenGroup {
  id: string;
  title: string;
  blurb: string;
  rows: TokenRow[];
}

export interface ConstantRow {
  token: string;
  role: string;
  value: string;
}

export interface ConstantGroup {
  id: string;
  title: string;
  blurb: string;
  rows: ConstantRow[];
}

/** Colour tokens — every one has a light and a dark value. */
export const TOKEN_GROUPS: TokenGroup[] = [
  {
    id: "surfaces",
    title: "Surfaces",
    blurb: "Grounds and hairlines, from the page backdrop up to the solid raised affordance.",
    rows: [
    { token: "--bg", role: "Page / canvas backdrop", light: "#FAFAF7", dark: "#0B0F19" },
    { token: "--bg-raised", role: "Nav rail, raised headers, panel ground", light: "#FFFFFF", dark: "#0D1220" },
    { token: "--surface", role: "Cards, dialogs, inset panels", light: "#F5F3EC", dark: "#111827" },
    { token: "--surface-raised", role: "Solid raised affordance — thead, input, dropdown row. Deliberately LIGHTER than --surface in dark: that is the raised relationship, not a bug", light: "#FFFFFF", dark: "#161E2E" },
    { token: "--rule", role: "Hairlines, dividers, table rules", light: "#E3E1D6", dark: "#1A2234" },
    { token: "--rule-bright", role: "Emphasis borders, form-control edges", light: "#D6D3C6", dark: "#222D40" },
    { token: "--canvas-bg", role: "Standalone diagram canvases", light: "#FAFAF7", dark: "#0B0F19" },
    ],
  },
  {
    id: "accents",
    title: "Semantic accents",
    blurb: "Each carries one meaning and a matching -dim fill. The -dim variants are what TONE_FILL in mono/primitives and every ds/ tone prop resolve to — they are not decorative alternates.",
    rows: [
    { token: "--slate", role: "Structure, chrome, scaffolding, primary action", light: "#5A6E8C", dark: "#3D4B6F" },
    { token: "--slate-dim", role: "Slate fill behind slate ink", light: "rgba(90,110,140,0.12)", dark: "rgba(61,75,111,0.15)" },
    { token: "--mustard", role: "Ranking, critical path, divergence, emphasis, focus ring", light: "#946500", dark: "#D4A017" },
    { token: "--mustard-dim", role: "Mustard fill behind mustard ink", light: "rgba(168,116,0,0.10)", dark: "rgba(212,160,23,0.12)" },
    { token: "--coral", role: "Action, urgency, warning, destructive", light: "#A85443", dark: "#C0533A" },
    { token: "--coral-dim", role: "Coral fill behind coral ink", light: "rgba(176,90,72,0.10)", dark: "rgba(192,83,58,0.12)" },
    { token: "--coral-text", role: "Coral as words: clay in light, --text-ter in Night, where clay is too dim for small text", light: "var(--coral)", dark: "var(--text-ter)" },
    { token: "--green", role: "Execution, health, confirmation, live state", light: "#3E6B52", dark: "#3A7D56" },
    { token: "--green-dim", role: "Green fill behind green ink", light: "rgba(62,107,82,0.10)", dark: "rgba(58,125,86,0.12)" },
    { token: "--green-text", role: "Green as words: forest in light, --text-ter in Night, where forest is for lines and fills", light: "var(--green)", dark: "var(--text-ter)" },
    ],
  },
  {
    id: "text",
    title: "Text ramp",
    blurb: "Four steps, primary to dimmest. The light values were darkened one stop each in 2026-08 for AA contrast — --text-dim went 2.6:1 → 4.8:1.",
    rows: [
    { token: "--text-pri", role: "Headings, active labels, values", light: "#1E2A44", dark: "#D8DDE8" },
    { token: "--text-sec", role: "Body copy, descriptions", light: "#3E4A63", dark: "#9BA5BC" },
    { token: "--text-ter", role: "Hints, mono eyebrows, inactive", light: "#5A6E8C", dark: "#8A96AB" },
    { token: "--text-dim", role: "Placeholders, quietest text", light: "#667080", dark: "#7C8AA3" },
    ],
  },
  {
    id: "hover",
    title: "Hover",
    blurb: "Neutral overlays, four weights. Never use a solid grey for hover in dark mode — solid grey is lighter than the surface, which is the classic \"hover too light\" bug.",
    rows: [
    { token: "--hover-faint", role: "Zebra banding — the lightest step", light: "rgba(0,0,0,0.018)", dark: "rgba(255,255,255,0.02)" },
    { token: "--hover-dim", role: "Barely-there row hover", light: "rgba(0,0,0,0.02)", dark: "rgba(255,255,255,0.03)" },
    { token: "--hover-tint", role: "Standard row / list-item hover", light: "rgba(0,0,0,0.035)", dark: "rgba(255,255,255,0.04)" },
    { token: "--hover-lift", role: "Stronger hover; also the skeleton fill", light: "rgba(0,0,0,0.055)", dark: "rgba(255,255,255,0.06)" },
    ],
  },
  {
    id: "active",
    title: "Active / selected",
    blurb: "Tinted rather than neutral, so selection reads as state rather than as hover.",
    rows: [
    { token: "--active-dim", role: "Quietest selected state", light: "rgba(90,110,140,0.08)", dark: "rgba(61,75,111,0.15)" },
    { token: "--active-tint", role: "Standard active / selected row", light: "rgba(90,110,140,0.12)", dark: "rgba(61,75,111,0.20)" },
    { token: "--active-lift", role: "Selected and hovered", light: "rgba(90,110,140,0.17)", dark: "rgba(61,75,111,0.25)" },
    { token: "--active-hi", role: "Strongest selection emphasis", light: "rgba(90,110,140,0.26)", dark: "rgba(61,75,111,0.40)" },
    ],
  },
];

/** Mode-independent tokens. */
export const CONSTANT_GROUPS: ConstantGroup[] = [
  {
    id: "type",
    title: "Type",
    blurb: "Four fonts, four jobs. Same in both modes.",
    rows: [
    { token: "--heading", role: "UI headings, section titles, nav", value: "'fieldwork', 'Work Sans', system-ui, sans-serif" },
    { token: "--sans", role: "Body copy, UI descriptions", value: "'degular-variable', -apple-system, BlinkMacSystemFont, 'DM Sans', system-ui, sans-serif" },
    { token: "--serif", role: "Editorial, display, panel titles, pull-quotes", value: "'Spectral', Georgia, serif" },
    { token: "--mono", role: "Labels, eyebrows, code, data, counts", value: "'JetBrains Mono', monospace" },
    ],
  },
  {
    id: "layout",
    title: "Layout metrics",
    blurb: "Shell dimensions referenced by the app chrome.",
    rows: [
    { token: "--sidebar-w", role: "Prioritization shell sidebar width", value: "256px" },
    { token: "--nav-h", role: "Top nav height", value: "64px" },
    ],
  },
];

/**
 * Tokens present in index.css that no group above claims. A non-empty list here
 * means index.css gained a token and the grouping in scripts/buildDesignTokens.ts
 * has not been updated to place it — the page would silently omit it.
 */
export const UNGROUPED_TOKENS: string[] = ["--command-bg","--command-danger","--command-hover","--command-rule","--command-shadow","--command-text","--command-text-dim"];

/**
 * The complete light palette as inline CSS custom properties.
 *
 * The design-language shell pins `.dark` for chrome parity, so a light preview
 * has to re-assert every token on a wrapper. Hand-maintaining this list is how
 * the page came to preview a light mode that no longer existed.
 */
export const LIGHT_VARS: Record<string, string> = {
  "--bg": "#FAFAF7",
  "--bg-raised": "#FFFFFF",
  "--surface": "#F5F3EC",
  "--surface-raised": "#FFFFFF",
  "--rule": "#E3E1D6",
  "--rule-bright": "#D6D3C6",
  "--canvas-bg": "#FAFAF7",
  "--slate": "#5A6E8C",
  "--slate-dim": "rgba(90,110,140,0.12)",
  "--mustard": "#946500",
  "--mustard-dim": "rgba(168,116,0,0.10)",
  "--coral": "#A85443",
  "--coral-dim": "rgba(176,90,72,0.10)",
  "--green": "#3E6B52",
  "--green-dim": "rgba(62,107,82,0.10)",
  "--text-pri": "#1E2A44",
  "--text-sec": "#3E4A63",
  "--text-ter": "#5A6E8C",
  "--text-dim": "#667080",
  "--green-text": "var(--green)",
  "--coral-text": "var(--coral)",
  "--cta": "#1E2A44",
  "--cta-hover": "#2E3C5C",
  "--cta-ink": "#FFFFFF",
  "--hover-tint": "rgba(0,0,0,0.035)",
  "--hover-dim": "rgba(0,0,0,0.02)",
  "--hover-lift": "rgba(0,0,0,0.055)",
  "--hover-faint": "rgba(0,0,0,0.018)",
  "--active-tint": "rgba(90,110,140,0.12)",
  "--active-dim": "rgba(90,110,140,0.08)",
  "--active-lift": "rgba(90,110,140,0.17)",
  "--active-hi": "rgba(90,110,140,0.26)",
  "--command-bg": "#1E2A44",
  "--command-rule": "#2E3C5C",
  "--command-text": "#FAFAF7",
  "--command-text-dim": "#B8C0D2",
  "--command-hover": "rgba(255,255,255,0.08)",
  "--command-shadow": "0 12px 40px rgba(30,42,68,0.35)",
  "--command-danger": "#E8846C"
};
